// Hosted-tools config ที่ใช้ร่วมกันระหว่าง
//   1) การ์ด 🛠️ Tools ของ ModelManagement.vue  → เก็บลง GPTModel.ToolsConfig (ระดับ bot)
//   2) Tools panel ของโหนด AI Model ใน Flow.vue → เก็บลง node.data.toolsConfig (ระดับ node)
// ทั้งคู่ผลิต/อ่าน JSON string ก้อนเดียวกัน แล้วหลังบ้าน (IntegrationLLMHub.BuildConfiguredTools)
// เอาไปประกอบเป็น tools payload ของ Responses API
//
// shape: { tool_choice, web_search: { allowed_domains, search_context_size, user_location } }
// เพิ่ม tool ชนิดใหม่: เพิ่มที่ TOOL_TYPE_OPTIONS + block ใน buildToolsConfig/parseToolsConfig
// แล้วเพิ่ม builder ฝั่ง backend ที่ BuildConfiguredTools

// จำกัด allowed_domains ไม่เกิน 20 (ตาม spec ของ web_search)
export const MAX_ALLOWED_DOMAINS = 20;

// tool type ที่เลือกได้ — ไม่มีค่า default (ว่าง = ไม่เก็บ tool ใด ๆ)
export const TOOL_TYPE_OPTIONS = [{ text: '🌐 web_search', value: 'web_search' }];

// tool_choice mode (optional) — ว่าง = ไม่ส่ง tool_choice ให้ provider ตัดสินใจเอง
export const TOOL_CHOICE_OPTIONS = [
  { text: 'web_search', value: 'web_search' },
  { text: 'auto', value: 'auto' },
  { text: 'none', value: 'none' },
];

export const SEARCH_CONTEXT_SIZE_OPTIONS = [
  { text: 'Low', value: 'low' },
  { text: 'Medium', value: 'medium' },
  { text: 'High', value: 'high' },
];

// ISO-3166-1 alpha-2 (common). combobox → free-type รหัสอื่นได้
export const WEB_SEARCH_COUNTRY_OPTIONS = ['TH', 'US', 'GB', 'JP', 'SG', 'HK', 'CN', 'TW', 'KR', 'IN', 'AU', 'DE', 'FR', 'IT', 'ES', 'NL', 'CA', 'BR', 'ID', 'MY', 'VN', 'PH'];

// City / Region ของ user_location เป็น free text (ไม่มี dropdown ให้เลือก) — OpenAI รับ free text
// แนะนำให้พิมพ์เป็นภาษาอังกฤษ ผลค้นหาจะ match ท้องถิ่นได้แม่นกว่า

// IANA timezones (common). combobox → free-type zone อื่นได้
export const WEB_SEARCH_TIMEZONE_OPTIONS = [
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Seoul',
  'Asia/Kolkata',
  'Australia/Sydney',
  'Europe/London',
  'Europe/Paris',
  'America/New_York',
  'America/Los_Angeles',
  'UTC',
];

// ฟอร์มเปล่าของ web_search — ต้องเรียกใหม่ทุกครั้ง (อย่าใช้ object ร่วมกันข้าม component/โหนด)
export function createWebSearchForm() {
  return {
    allowedDomains: [],
    searchContextSize: null,
    locationCountry: null,
    locationCity: null,
    locationRegion: null,
    locationTimezone: null,
  };
}

// ตัดรายการโดเมนไม่ให้เกิน MAX_ALLOWED_DOMAINS — เรียกจาก v-combobox ตอนเพิ่ม tag
// (ไม่เกินลิมิต → คืน reference เดิม ไม่ trigger การ re-render โดยไม่จำเป็น)
export function capAllowedDomains(value) {
  if (!Array.isArray(value)) return [];
  return value.length > MAX_ALLOWED_DOMAINS ? value.slice(0, MAX_ALLOWED_DOMAINS) : value;
}

// ประกอบ tools config เป็น JSON string; null ถ้าไม่ได้เลือก type ไว้
// เก็บ allowed_domains ระดับ web_search — หลังบ้าน (BuildWebSearchTool) จะห่อเป็น filters.allowed_domains เอง
export function buildToolsConfig(toolType, toolChoice, webSearchForm) {
  const root = {};

  // web_search: เก็บเฉพาะเมื่อเลือก type = web_search (type เคลียร์ได้ = ไม่เก็บ)
  if (toolType === 'web_search') {
    const ws = webSearchForm || {};
    const cfg = {};
    const allowed = (ws.allowedDomains || []).map((d) => String(d).trim()).filter(Boolean).slice(0, MAX_ALLOWED_DOMAINS);
    if (allowed.length) cfg.allowed_domains = allowed;
    if (ws.searchContextSize) cfg.search_context_size = ws.searchContextSize;
    // user_location: รวม 4 ฟิลด์เป็น object เดียว; ถ้าว่างหมด → ตัด key ทิ้ง
    const loc = {};
    if (ws.locationCountry) loc.country = String(ws.locationCountry).trim();
    if (ws.locationCity) loc.city = String(ws.locationCity).trim();
    if (ws.locationRegion) loc.region = String(ws.locationRegion).trim();
    if (ws.locationTimezone) loc.timezone = String(ws.locationTimezone).trim();
    if (Object.keys(loc).length) cfg.user_location = loc;
    // เลือก type ไว้แล้วต้องเก็บ key web_search เสมอ แม้ยังไม่ตั้ง param ใด (หลังบ้านจะ inject tool ให้)
    root.web_search = cfg;

    // tool_choice (optional): 'web_search' → {type:'web_search'}; auto/none → string; ไม่เลือก = ไม่เก็บ
    if (toolChoice === 'web_search') root.tool_choice = { type: 'web_search' };
    else if (toolChoice) root.tool_choice = toolChoice;
  }

  if (!Object.keys(root).length) return null;
  return JSON.stringify(root);
}

// แตก ToolsConfig JSON กลับเป็นค่าในฟอร์ม (ทนต่อ config เสีย → คืนค่า default)
// คืน toolType ด้วย เพื่อให้ฟอร์มกลับมาแสดงชนิด tool ที่เคยตั้งไว้ (ไม่งั้น save ทับแล้ว config หาย)
export function parseToolsConfig(raw) {
  const webSearchForm = createWebSearchForm();
  // ไม่ default เป็น web_search — bot/โหนดที่ยังไม่เคยตั้งค่าต้องขึ้นช่องว่าง
  let toolType = null;
  let toolChoice = null;

  try {
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed) {
      // tool_choice: object {type:'web_search'} → 'web_search'; string auto/none/required → ตรงตัว
      const tc = parsed.tool_choice;
      if (typeof tc === 'string' && tc) toolChoice = tc;
      else if (tc && tc.type) toolChoice = tc.type;

      const c = parsed.web_search || null;
      if (c) {
        toolType = 'web_search';
        webSearchForm.allowedDomains = Array.isArray(c.allowed_domains) ? c.allowed_domains.slice(0, MAX_ALLOWED_DOMAINS) : [];
        // เว้นว่างได้ (clearable) → null = ไม่ส่ง search_context_size ให้ OpenAI ใช้ default (medium)
        webSearchForm.searchContextSize = c.search_context_size || null;
        const loc = c.user_location || {};
        webSearchForm.locationCountry = loc.country || null;
        webSearchForm.locationCity = loc.city || null;
        webSearchForm.locationRegion = loc.region || null;
        webSearchForm.locationTimezone = loc.timezone || null;
      }
    }
  } catch (e) {
    // config เสีย → คงค่า default
  }

  return { toolType, toolChoice, webSearchForm };
}
